import { Component, OnDestroy, OnInit } from '@angular/core';
import { ITableConfig } from 'projects/common/src/app/interfaces/table/table-config';
import { ITableDataParams } from 'projects/common/src/app/interfaces/table/table-data-params';
import { ITableHeaderParams } from 'projects/common/src/app/interfaces/table/table-header-params';
import { CacheService } from 'projects/common/src/app/services';
import { LanguageService } from 'projects/common/src/app/services/parameters/language/language.service';
import { TableConfigService } from 'projects/common/src/app/services/table/table-config.service';
import { ExcelExportService } from 'projects/common/src/app/services/utils/excel-export.service';
import { excelExportDateFormat } from 'projects/common/src/app/services/utils/util.service';
import { Subscription } from 'rxjs';
import { ICheckFilter } from 'src/app/modules/core/interfaces/check-filter';
import { IFilterConfig } from 'src/app/modules/core/interfaces/filter-config';
import { IOutputFilter } from 'src/app/modules/core/interfaces/output-filter';
import { IResponse } from 'src/app/modules/core/interfaces/response';
import { DateTimePresentationPipe } from 'src/app/modules/core/pipes/date-time-presentation.pipe';
import { FilterHandlerService } from 'src/app/modules/core/services/filter/filter-handler.service';
import { GoogleAnalyticsService } from 'src/app/modules/core/services/google/google-analytics.service';
import { ILayoutConfig } from 'src/app/modules/layout/interfaces/layout-config';
import { LayoutConfigService } from 'src/app/modules/layout/services/layout-config.service';
import { ICas } from '../../interfaces/report-cas';
import { ReportCasService } from '../../services/report-cas.service';

@Component({
  selector: 'app-report-cas',
  templateUrl: './report-cas.component.html',
  styleUrls: ['./report-cas.component.scss'],
})
export class ReportCasComponent implements OnInit, OnDestroy {
  public tableConfig: ITableConfig;
  public showFilter: ICheckFilter;
  public selectedFilters: IOutputFilter;
  private readonly subscriptions;
  public message: string = null;
  public canDownload = false;
  public hasData = false;
  public isLhd: boolean;
  public systemError: string;
  public filterConfig: IFilterConfig;
  public tableData: ICas[];
  public tableDataFiltered: ICas[];
  public tableDataSearched: ICas[];
  public searchText: string;
  public option1 = false;
  public option2 = false;
  public imageKey1: string;
  public imageKey2: string;
  public imageType: string;
  public btnSpinner: boolean;
  public disabledExcelBtn: boolean;

  constructor(
    protected layoutConfigService: LayoutConfigService,
    protected languageService: LanguageService,
    protected casService: ReportCasService,
    protected filterHandlerService: FilterHandlerService,
    protected cacheService: CacheService,
    protected datePipePresentation: DateTimePresentationPipe,
    private tableConfigService: TableConfigService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private _excelExportService: ExcelExportService,
  ) {
    this.declareFilters();
    this.tableData = [];
    this.tableDataFiltered = [];
    this.tableDataSearched = [];
    this.subscriptions = new Subscription();
    this.imageKey1 = 'person-gray';
    this.imageKey2 = 'truck-gray';
    this.imageType = 'icon';
    this.disabledExcelBtn = false;
  }

  /**
   * On init component
   */
  public ngOnInit(): void {
    this.isLhd = true;
    this.initLayoutTitle();
  }
  /**
   * Function to declare filter to show
   */
  protected declareFilters(): void {
    this.filterConfig = {
      dates: {
        required: true,
        options: {
          daysRange: 0.5,
          minDayRange: 1,
        },
      },
      machineType: { required: true },
      machineList: { required: true },
      operator: { required: false },
      cleanButton: true,
    };
  }

  /**
   * Function to select icon image, title & subtitle
   */
  protected initLayoutTitle(): void {
    const subtitle = 'report-cas';
    const config: ILayoutConfig = {
      title: 'reports',
      subtitle,
      imageKey: 'report',
      imageType: 'section',
    };
    this.layoutConfigService.setConfig(config);
  }

  /**
   * Function to get report data from received filters
   *
   * @param filters Filters selected to get data
   */
  public getReportCas(selectedFilters: IOutputFilter): void {
    this.filterConfig.isLoading = true;
    if (selectedFilters) {
      this.selectedFilters = {
        startDate: selectedFilters.startDate,
        machineId: selectedFilters.machineId,
        machineTypeId: selectedFilters.machineTypeId,
      };
      this.casService.getCas(this.selectedFilters).subscribe(
        (response) => {
          this.tableData = [];
          if (response?.data.length > 0) {
            response?.data.forEach((td) => {
              this.tableData.push({
                alarmStatusName: td.alarmStatusName,
                tagId: td.tagId,
                tagType: this.languageService.message(td.tagType.toLowerCase()).toUpperCase(),
                associatedName: td.associatedName,
                rssi: td.rssi,
                timestamp: td.timestamp,
                antennaName: td.antennaName,
                operatorName: td.operatorName,
                alarmStatusId: td.alarmStatusId,
                antennaId: td.antennaId,
              });
            });
            this.setTableConfig(this.tableData);
            this.canDownload = true;
            this.hasData = true;
            this.message = null;
            this.filterConfig.isLoading = false;
          } else {
            this.hasData = false;
            this.canDownload = false;
            this.message = this.languageService.message('m034');
            this.filterConfig.isLoading = false;
          }
          this.systemError = '';
          this.filterConfig.isLoading = false;
        },
        (err) => {
          this.message = '';
          this.hasData = false;
          this.canDownload = false;
          this.message = this.languageService.message('m034');
          this.filterConfig.isLoading = false;
        },
      );
    }
    const filters = {
      reports: 'report-cas',
    };
    this.googleAnalyticsService.sendCustomEvent('filter', filters);
  }

  /**
   * Function to download report in excel format
   */
  public downloadExcel(): void {
    const selectRangeDate = {
      startDate: excelExportDateFormat(this.selectedFilters.startDate),
      endDate: excelExportDateFormat(this.selectedFilters.startDate),
    };

    this.btnSpinner = true;
    this.disabledExcelBtn = true;
    const sub = this.casService.getCasExcel(this.selectedFilters).subscribe({
      next: (response) => {
        this._excelExportService.exportAsExcel(response, 'report-cas', selectRangeDate, null, false);
        this.btnSpinner = false;
        this.disabledExcelBtn = false;
        this._excelExportService.messageToastSuccess();
      },
      error: (response: IResponse) => {
        console.error(response.error);
        this.btnSpinner = false;
        this.disabledExcelBtn = false;
        this._excelExportService.messageToastError();
      },
    });

    this.subscriptions.add(sub);
  }

  /**
   * Destroy subscription
   */
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Fill tabla
   *
   * @param tableData
   */
  public setTableConfig(tableData: ICas[]) {
    const headerParams: ITableHeaderParams = {
      data: tableData,
      excludeColumns: ['alarmStatusId', 'antennaId'],
      sortables: true,
      tooltip: [{ field: 'operatorName', text: this.languageService.message('tooltip-text-cas'), marginTop: '3px', marginRight: '3px' }],
    };
    const datasetParams: ITableDataParams = {
      tableData,
      excludeColumns: ['alarmStatusId', 'antennaId'],
      dateConfig: [{ field: 'timestamp', format: 'date-time' }],
      imageConfig: [
        {
          field: 'tagType',
          imageType: 'icon',
          fieldReplace: false,
          isImageStatus: false,
          fieldReplaceName: false,
          isIconList: true,
          iconList: [
            {
              imageType: 'icon',
              imageName: 'person-gray',
              imageWidth: '20px',
              imageHeight: '20px',
              margin: '0px 0px 0px 10px',
              iconName: 'PERSONA',
            },
            {
              imageType: 'icon',
              imageName: 'truck-gray',
              imageWidth: '20px',
              imageHeight: '20px',
              margin: '0px 0px 0px 10px',
              iconName: 'VEHICULO',
            },
          ],
        },
      ],
      badge: [
        {
          field: 'alarmStatusName',
          badgeList: [
            {
              class: 'badge badge-pill badge-primary',
              color: '#FFFFFF',
              backgroundColor: '#22D983',
              badgeName: 'VERDE',
              badgeNameParent: 'VERDE',
              width: '90px',
              height: '20px',
            },
            {
              class: 'badge badge-pill badge-primary',
              color: '#FFFFFF',
              backgroundColor: ' #F7D76D',
              badgeName: 'AMARILLO',
              badgeNameParent: 'AMARILLO',
              width: '90px',
              height: '20px',
            },
            {
              class: 'badge badge-pill badge-primary',
              color: '#FFFFFF',
              backgroundColor: '#F05050',
              badgeName: 'ROJO',
              badgeNameParent: 'ROJO',
              width: '90px',
              height: '20px',
            },
          ],
        },
      ],
    };

    const header = this.tableConfigService.setHeaderConfig(headerParams);
    const dataset = this.tableConfigService.setDatasetConfig(datasetParams);

    this.tableConfig = { header, dataset };
  }

  public searchTable() {
    this.tableDataSearched = [];
    this.tableDataSearched = this.tableDataFiltered.length > 0 ? this.tableDataFiltered : this.tableData;
    if (this.searchText !== '') {
      this.tableDataSearched = this.tableDataSearched.filter((a: any) => {
        for (const key in a) {
          if (Object.prototype.hasOwnProperty.call(a, key)) {
            const element = a[key];
            if (element.toString().toUpperCase() === this.searchText.toUpperCase()) {
              return true;
            }
          }
        }
        return false;
      });
    } else {
      this.tableDataSearched = this.tableDataFiltered.length > 0 ? this.tableDataFiltered : this.tableData;
    }
    this.setTableConfig(this.tableDataSearched);
  }
  public toggleChangeDataTable(option: string) {
    this.tableDataFiltered = [];
    this.tableDataFiltered = this.tableDataSearched.length > 0 ? this.tableDataSearched : this.tableData;
    if (option === 'option1') {
      if (!this.option1) {
        this.imageKey1 = 'person-white';
        this.imageKey2 = 'truck-gray';
        this.option2 = false;
        this.tableDataFiltered = this.tableDataFiltered.filter((a: any) => a.tagType === 'PERSONA');
      } else {
        this.tableDataFiltered = this.tableDataSearched.length > 0 ? this.tableDataSearched : this.tableData;
        this.imageKey1 = 'person-gray';
      }
    } else if (!this.option2) {
      this.option1 = false;
      this.imageKey1 = 'person-gray';
      this.imageKey2 = 'truck-white';
      this.tableDataFiltered = this.tableDataFiltered.filter((a: any) => a.tagType === 'VEHICULO');
    } else {
      this.tableDataFiltered = this.tableDataSearched.length > 0 ? this.tableDataSearched : this.tableData;
      this.imageKey2 = 'truck-gray';
    }
    this.setTableConfig(this.tableDataFiltered);
  }
}
